/*
 * @Description:
 * @Author: Jack Deng
 * @Date: 2021-11-16 17:47:40
 * @LastEditors: Jack Deng
 * @LastEditTime: 2021-11-23 13:18:14
 * @FilePath: /unifyxp-v2-store-portal/src/pages/SalesChannelContentEditor/SalesChannelEditorHeader/slice/index.ts
 */
import { isCurrentPrimary } from '@/src/constrains/SalesChannelMultiLanguageSelect';
import {
  getSalesChannelContentHeader,
  postSalesChannelContentHeader,
} from '@/src/request/salesChannel/content';
import { randomNumber } from '@/src/tools/helper';
import { msg } from '@/src/tools/message';
import { getI18nFromGeneral } from '@/src/tools/tools';
import {
  createAsyncThunk,
  createSelector,
  createSlice,
} from '@reduxjs/toolkit';
import arrayMove from 'array-move';
import { set } from 'lodash';
import {
  generateDefaultState as _generateDefaultState,
  onCollapseOpen as _onCollapseOpen,
  assData,
  defaultStatus,
  hasError,
} from 'src/pages/SalesChannelContentEditor/slice/reduxCommonFunctions';
import {
  EnumReducers,
  getCollapseOpenStatus as _getCollapseOpenStatus,
  getCollapseValidationMsgDisplay as _getCollapseValidationMsgDisplay,
  getGeneralFeaturedProduct as _getGeneralFeaturedProduct,
  getHasModified as _getHasModified,
  getLoaded as _getLoaded,
  getSalesChannel as _getSalesChannel,
  getSalesChannelName as _getSalesChannelName,
  getState as _getState,
  getSubmitStamp as _getSubmitStamp,
} from 'src/pages/SalesChannelContentEditor/slice/selectors'; //
import {
  convertDataFromServerToRedux,
  fixedCollapse,
  generateHeaderStyle,
  generateHoverStyle,
  generatePromotionBarStyle,
  generateScrollEffect,
} from '../tool';

export const fetchHeader = createAsyncThunk(
  'fetchSalesChannelContentHeader',
  async (data: any) => {
    const { uuid, params } = data;
    const isPrimary = isCurrentPrimary();
    const response = await getSalesChannelContentHeader(uuid, params);
    return {
      response,
      isPrimary,
    };
  },
);

export const submitHeader = createAsyncThunk(
  'submitSalesChannelCotentHeader',
  async ({ body, params }: any) => {
    const uuid = body?.header?.contentUuid;
    const response = await postSalesChannelContentHeader(uuid, body, params);
    return {
      response,
    };
  },
);

const _defaultState = _generateDefaultState(fixedCollapse);

const defaultState = {
  ...defaultStatus,
  ..._defaultState,
  promotionBarStyle: generatePromotionBarStyle(),
  headerStyle: generateHeaderStyle(),
  hoverStyle: generateHoverStyle(),
  scrollEffect: generateScrollEffect(),
  promotionBars: [],
  newPromotionBar: {},
};

const salesChannelContentHeaderPage = createSlice({
  name: 'salesChannelContentHeaderPage',
  initialState: defaultState,
  reducers: {
    resetData(state) {
      assData(state, defaultState);
    },
    onCollapseOpen(state, action) {
      const { idArr = [] } = action.payload; //=>[id:string]
      if (
        state.newPromotionBar.uuid &&
        idArr.indexOf(state.newPromotionBar.uuid) === -1
      ) {
        state.promotionBars.push(state.newPromotionBar);
        state.newPromotionBar = {};
      }
      _onCollapseOpen(state, idArr);
    },
    onErrorChange(state, action) {
      const { id, status } = action.payload;
      state.collapseErrorMsgDisplay[id].hasError = status;
    },
    onFixedFieldChange(state, action) {
      const { path, value } = action.payload;
      set(state, path, value);
      state.hasModified = true;
    },
    onChangeItemProp(state, action) {
      const { value, prop, uuid, isNew } = action.payload;
      const newPromotion = state.newPromotionBar;
      const index = state.promotionBars.findIndex((v) => v.uuid === uuid);
      if (isNew && index === -1) {
        state.newPromotionBar[prop] = value;
        state.promotionBars.push(newPromotion); //UXPSA-11599
        state.newPromotionBar = {};
      } else {
        state.promotionBars[index][prop] = value;
      }
      state.hasModified = true;
    },
    onAddNewItem(state, action) {
      if (Object.values(state.newPromotionBar).length === 0) {
        const {} = action.payload;
        const promotionBars = state.promotionBars;
        const id = randomNumber();
        state.collapseErrorMsgDisplay[id] = { hasError: false, display: false };
        state.collapseOpen[id] = true;
        state.newPromotionBar = {
          isNew: true,
          uuid: id,
          name: `PromotionBar ${promotionBars.length + 1}`,
        };
      } else {
        const newPromotion = state.newPromotionBar;
        state.promotionBars.push(newPromotion);
        const id = randomNumber();
        const promotionBars = state.promotionBars;
        state.collapseErrorMsgDisplay[id] = { hasError: false, display: false };
        state.collapseOpen[newPromotion.uuid] = false;
        state.collapseErrorMsgDisplay[newPromotion.uuid].display = true;
        state.collapseOpen[id] = true;
        state.newPromotionBar = {
          isNew: true,
          uuid: id,
          name: `PromotionBar ${promotionBars.length + 1}`,
        };
      }
      state.hasModified = true;
    },
    onDragSorting(state, action) {
      state.hasModified = true;
      const { oldIndex, newIndex } = action.payload;
      state.hasModified = true;
      state.promotionBars = arrayMove(state.promotionBars, oldIndex, newIndex);
    },
    onCloneItem(state, action) {
      const { uuid } = action.payload;
      const id = randomNumber();
      const item = state.promotionBars.find((v) => v.uuid === uuid);
      state.collapseErrorMsgDisplay[id] = { hasError: false, display: true };
      state.collapseOpen[id] = true;
      state.promotionBars.push({
        ...item,
        name: `${item.name}-cloned`,
        uuid: id,
        isNew: true,
        beginTime: null,
        beginDate: null,
        endTime: null,
        endDate: null,
      });
      state.hasModified = true;
    },
    onDeleteItem(state, action) {
      const { uuid } = action.payload;
      const index = state.promotionBars.findIndex((v) => v.uuid === uuid);
      if (index !== -1) {
        state.promotionBars.splice(index, 1);
        delete state.collapseErrorMsgDisplay[uuid];
        delete state.collapseOpen[uuid];
      }
      state.hasModified = true;
    },
    resetConfig(state, action) {
      const { key } = action.payload;
      switch (key) {
        case 'promotionBarStyle': {
          state[key] = generatePromotionBarStyle();
          break;
        }
        case 'headerStyle': {
          state[key] = generateHeaderStyle();
          break;
        }
        case 'hoverStyle': {
          state[key] = generateHoverStyle();
          break;
        }
        case 'scrollEffect': {
          state[key] = generateScrollEffect();
          break;
        }
      }
    },
    validation(state) {
      hasError(state, state.collapseErrorMsgDisplay || {});
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchHeader.fulfilled, (state, action) => {
      const { response } = action.payload;
      const _data = convertDataFromServerToRedux(response) || {};
      assData(state, _data);
      state.newPromotionBar = {};
      state.loaded = true;
    });
    builder.addCase(fetchHeader.pending, (state, action) => {
      state.loaded = false;
    });
    builder.addCase(fetchHeader.rejected, (state, action) => {
      state.loaded = true;
    });
    builder.addCase(submitHeader.fulfilled, (state) => {
      msg.success(getI18nFromGeneral('general.status.changessavesuccessfully'));
      state.loaded = true;
      state.hasModified = false;
    });
    builder.addCase(submitHeader.pending, (state) => {
      state.loaded = false;
    });
    builder.addCase(submitHeader.rejected, (state) => {
      state.loaded = true;
    });
  },
});

export const getLoaded = () => {
  return _getLoaded(EnumReducers.header);
};

export const getSubmitStamp = () => {
  return _getSubmitStamp(EnumReducers.header);
};

export const getCollapseOpenStatus = (id) => {
  return _getCollapseOpenStatus(EnumReducers.header, id);
};

export const getCollapseValidationMsgDisplay = (id) => {
  return _getCollapseValidationMsgDisplay(EnumReducers.header, id);
};

export const getGeneralFeaturedProduct = () => {
  return _getGeneralFeaturedProduct(EnumReducers.header);
};

export const getState = () => {
  return _getState(EnumReducers.header);
};

export const getHasModified = () => {
  return _getHasModified(EnumReducers.header);
};

export const getSalesChannel = () => {
  return _getSalesChannel(EnumReducers.header);
};

export const getSalesChannelName = () => {
  return _getSalesChannelName(EnumReducers.header);
};

export const getTimeZone = () => {
  return createSelector(
    (state: any) => state.salesChannelContent[EnumReducers.header].timezone,
    (state: any) =>
      state.salesChannelContent[EnumReducers.header].timezoneInGmt,
    (a, b) => `${a || ''} ${b || ''}`,
  );
};

export const getPromotionBarStyle = () => {
  return createSelector(
    (state: any) =>
      state.salesChannelContent[EnumReducers.header].promotionBarStyle,
    (v) => v,
  );
};

export const getHeaderStyle = () => {
  return createSelector(
    (state: any) => state.salesChannelContent[EnumReducers.header].headerStyle,
    (v) => v,
  );
};

export const getHoverStyle = () => {
  return createSelector(
    (state: any) => state.salesChannelContent[EnumReducers.header].hoverStyle,
    (v) => v,
  );
};

export const getScrollEffect = () => {
  return createSelector(
    (state: any) => state.salesChannelContent[EnumReducers.header].scrollEffect,
    (v) => v,
  );
};

export const getNewPromotion = () => {
  return createSelector(
    (state: any) =>
      state.salesChannelContent[EnumReducers.header].newPromotionBar,
    (v) => v,
  );
};

export const getPromotionBarContents = () => {
  return createSelector(
    (state: any) =>
      state.salesChannelContent[EnumReducers.header].promotionBars,
    (v) => {
      return v;
    },
  );
};

export const getPromotionBarContent = (uuid: string, isNew: boolean) => {
  return createSelector(
    (state: any) =>
      state.salesChannelContent[EnumReducers.header].promotionBars,
    (state: any) =>
      state.salesChannelContent[EnumReducers.header].newPromotionBar,
    (list: Array<any>, newOne: any) => {
      const item = list.find((v) => v.uuid === uuid);
      return isNew ? newOne : item;
    },
  );
};

export const {
  resetData,
  onErrorChange,
  onCollapseOpen,
  onFixedFieldChange,
  onChangeItemProp,
  onAddNewItem,
  resetConfig,
  validation,
  onCloneItem,
  onDeleteItem,
  onDragSorting,
} = salesChannelContentHeaderPage.actions;

export default salesChannelContentHeaderPage.reducer;
